import { queriesAccounts, queriesPayStatements, queriesWorkLogs } from "@query";
import {
  WSGrid,
  WSList,
  WSPage,
  WSPageToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import { ListAuditLogsFilterParameterResourceTypeEq } from "@wingspanhq/wingspan-api-sdk";
import React from "react";

import { WSQueries } from "../../../../query/WSQuery";
import { ResourceHistory } from "../../../../shared/components/ResourceHistory";
import { useModalResourceDrillDownHistory } from "../../../../shared/components/ResourceHistory/ModalResourceDrillDownHistory";
import { PaystubDeductionsCard } from "../../../PayrollRuns/components/PaystubDeductionsCard";
import { PaystubIncomeCard } from "../../../PayrollRuns/components/PaystubIncomeCard";
import { useMutationDownloadPaystubPdf } from "../../../PayrollRuns/hooks/useMutationDownloadPaystubPdf";
import { selectorPaystubDetails } from "../../../PayrollRuns/selectors/selectorPaystubDetails";
import { PaystubDetailsCard } from "../../components/PaystubDetailsCard";
import { PaystubWorkLogsCard } from "../../components/PaystubWorkLogsCard";
import {
  PaystubsYear,
  routeHelperPaystubsDashboard,
  routeHelperPaystubsDetails
} from "../../paths";

export const RoutePaystubsDetails: React.FC = () => {
  const goToDashboard = routeHelperPaystubsDashboard.useGoTo();
  const modalResourceDrillDownHistory = useModalResourceDrillDownHistory();
  const [downloadPaystubPdf, downloadPaystubPdfMeta] =
    useMutationDownloadPaystubPdf();
  const {
    params: { year, paystubId }
  } = routeHelperPaystubsDetails.useData();

  const payStatementResource = {
    resourceId: paystubId,
    resourceType: ListAuditLogsFilterParameterResourceTypeEq.PAY_STATEMENT
  };

  const queryPayStatement = queriesPayStatements.getPayStatement.useQuery({
    payStatementId: paystubId
  });
  const payPeriodId = queryPayStatement.data?.payPeriodId ?? undefined;

  // Secondary queries stay OUTSIDE `WSQueries` (as unconditional, `enabled`-gated
  // hooks) — none becomes `isSuccess` on its own, so they'd otherwise keep
  // `WSQueries` stuck on the loader. Their data is consumed with fallbacks below.
  //
  // This statement's source work logs, scoped server-side via
  // `filter[payPeriodId][eq]`.
  const queryWorkLogs = queriesWorkLogs.listWorkLogs.useQueryAll(
    { filter: { payPeriodId: { eq: payPeriodId as string } } },
    { enabled: !!payPeriodId }
  );

  // The paying company ("Created by {company}" / employer name) is a separate
  // read — the pay statement carries only the payer's account id.
  const payerAccountId = queryPayStatement.data?.payerAccountId ?? undefined;
  const queryPayerAccount = queriesAccounts.getAccount.useQuery(
    { accountId: payerAccountId ?? "" },
    { enabled: !!payerAccountId }
  );

  return (
    <WSQueries queries={{ queryPayStatement }}>
      {({ queryPayStatementData }) => {
        const payPeriodWorkLogs = queryWorkLogs.flatData ?? [];

        const paystub = selectorPaystubDetails({
          payStatement: queryPayStatementData,
          workLogs: payPeriodWorkLogs,
          payerAccount: queryPayerAccount.data
        });

        const fileId = queryPayStatementData.fileId ?? undefined;
        const handleDownloadPdf = () =>
          fileId &&
          downloadPaystubPdf({
            fileId,
            filename: `paystub-${paystub.paystubNumber}`
          });

        return (
          <WSPage>
            <WSPageToolbar
              actions={[
                {
                  label: "Download PDF",
                  buttonKind: "Secondary",
                  disabled: !fileId,
                  loading: downloadPaystubPdfMeta.isLoading,
                  onClick: handleDownloadPdf
                }
              ]}
              breadcrumbs={[
                {
                  label: "Paystubs",
                  onClick: () => goToDashboard({ year: year as PaystubsYear })
                },
                { label: `Paystub #${paystub.paystubNumber}` }
              ]}
              title={`Paystub #${paystub.paystubNumber}`}
              titleAfter={
                <WSText.ParagraphXs color="gray500">
                  Created by {paystub.createdBy}
                </WSText.ParagraphXs>
              }
            />

            <WSGrid gutter="L" mt="XL">
              <WSGrid.Item span={{ s: "12", l: "8" }}>
                <WSList gap="XL">
                  <PaystubIncomeCard
                    earnings={paystub.earnings}
                    grossPayThisPeriod={paystub.grossPayThisPeriod}
                    grossPayYearToDate={paystub.grossPayYearToDate}
                  />
                  <PaystubDeductionsCard
                    deductionTotalThisPeriod={paystub.deductionTotalThisPeriod}
                    deductionTotalYearToDate={paystub.deductionTotalYearToDate}
                    netPayThisPeriod={paystub.netPayThisPeriod}
                    netPayYearToDate={paystub.netPayYearToDate}
                    payeeTaxes={paystub.payeeTaxes}
                    payoutMethodLabel={paystub.payoutMethodLabel}
                  />
                </WSList>
              </WSGrid.Item>

              <WSGrid.Item span={{ s: "12", l: "4" }}>
                <WSList gap="L">
                  {/* The payer company is the payee's employer. */}
                  <PaystubDetailsCard
                    employerName={paystub.createdBy}
                    paidDate={paystub.paidDate}
                    payPeriodRange={paystub.payPeriodRange}
                  />
                  <PaystubWorkLogsCard
                    workLogs={paystub.workLogs}
                    workLogsTotal={paystub.workLogsTotal}
                  />
                  <ResourceHistory
                    resources={[payStatementResource]}
                    onViewAll={() =>
                      modalResourceDrillDownHistory.open({
                        resource: payStatementResource,
                        title: "Paystub history"
                      })
                    }
                  />
                </WSList>
              </WSGrid.Item>
            </WSGrid>
          </WSPage>
        );
      }}
    </WSQueries>
  );
};
